import { action, computed, makeObservable, observable } from 'mobx';
import { Aggregate } from 'edit/Aggregate';
import type { EditableEntity } from 'edit/EditableEntity';
import { EditableRetentionPolicy } from 'edit/retentionPolicy/EditableRetentionPolicy';
import type { SaveZone, Zone } from 'api/zones/endpoints/postZone';
import { getZoneAuditTrail } from 'api/zones/endpoints/getZoneAuditTrail';
import type { AuditTrail } from 'api/shared/auditTrail';
import { toError } from 'helpers/toError';
import { acceptDegraded } from 'validation';

// The zone's change history, owned here so the audit view is a pure observer.
export type AuditTrailState = 'loading' | AuditTrail | Error;

/**
 * A zone open for editing: its own fields plus a one-to-many aggregate of
 * retention policies. Built directly from the loaded wire record (or `null` for
 * a blank create form); the model *is* the editable state, projecting back to
 * the request body via `toSave`.
 *
 * `original` is the pre-edit identity (the zone's `name`) the PUT addresses and
 * the create-vs-update branch keys on; `null` means a not-yet-saved zone. It is
 * written only internally (via `_init`), so it is exposed read-only, and flips
 * to a real identity after the first successful save.
 */
export class EditableZone implements EditableEntity<Zone | null> {
  private _auditTrail: AuditTrailState = 'loading';
  description: string | null = null;
  name: string | null = null;
  private _original: { name: string } | null = null;
  private readonly _retentionPolicies = new Aggregate<EditableZone, EditableRetentionPolicy>(this, (aggregate) => new EditableRetentionPolicy(aggregate));
  private _snapshot = '';

  constructor(loaded: Zone | null) {
    makeObservable<EditableZone, '_original' | '_auditTrail'>(this, {
      _auditTrail: observable.ref,
      description: observable,
      name: observable,
      _original: observable.ref,
      isDirty: computed,
    });
    this._init(loaded);
  }

  /**
   * The zone's audit trail — loaded eagerly when the entity opens in edit mode
   * (and reloaded after each save), so the Audit Trail tab is a pure observer
   * with no fetch state of its own. `'loading'` until the first load resolves.
   */
  get auditTrail(): AuditTrailState {
    return this._auditTrail;
  }

  get isCreating(): boolean {
    return this._original === null;
  }

  get isDirty(): boolean {
    return this._original === null || JSON.stringify(this.toSave()) !== this._snapshot;
  }

  /** Pre-edit identity (`null` while creating): the name the PUT addresses. */
  get original(): { name: string } | null {
    return this._original;
  }

  get retentionPolicies(): readonly EditableRetentionPolicy[] {
    return this._retentionPolicies.children;
  }

  createRetentionPolicy = (): EditableRetentionPolicy => this._retentionPolicies.create();

  reinitialize = (loaded: Zone | null): void => {
    this._init(loaded);
  };

  toSave(): SaveZone {
    return {
      name: this.name,
      // The backend PUT COALESCEs description (a null `$2` means "keep current"),
      // so send '' — not null — to clear it; the model always asserts the full
      // field value, and EditText yields null for an emptied field.
      description: this.description ?? '',
      retention_policies: this._retentionPolicies.children.map((p) => p.toSave()),
    };
  }

  // Shared by the constructor and `reinitialize`. Rebuilds the retention children
  // in place (same aggregate instance, so reactivity survives) and snapshots the
  // save body for dirty tracking. Wrapped in `action` so the whole rebuild lands
  // as one transaction — observers see only the final state.
  private readonly _init = action((loaded: Zone | null): void => {
    this._retentionPolicies.clear();
    if (loaded !== null) {
      this._original = { name: loaded.name };
      this.name = loaded.name;
      this.description = loaded.description;
      for (const wire of loaded.retention_policies) {
        this._retentionPolicies.append(new EditableRetentionPolicy(this._retentionPolicies, wire));
      }
      // Existing zone → load its history (fire-and-forget; sets the observable).
      void this._loadAuditTrail(loaded.name);
    } else {
      this._original = null;
      this.name = null;
      this.description = null;
    }
    this._snapshot = JSON.stringify(this.toSave());
  });

  private _loadAuditTrail = async (name: string): Promise<void> => {
    this._auditTrail = 'loading';
    try {
      this._auditTrail = acceptDegraded(await getZoneAuditTrail(name));
    } catch (err) {
      this._auditTrail = toError(err);
    }
  };
}
